import { useEffect, useRef, useState } from 'react'
import { supabase } from '@/config/supabase'
import type { RealtimeChannel } from '@supabase/supabase-js'

interface LeadWithValuation {
  id: string
  email: string
  phone: string | null
  first_name: string | null
  last_name: string | null
  gdpr_consent: boolean
  marketing_consent: boolean
  status: string
  notes: string | null
  created_at: string
  valuation_id: string | null
  valuation: {
    address: string
    area: number
    estimated_value: number
    min_value: number
    max_value: number
    price_per_sqm: number
    zone_name_csv: string
    property_type: string
    floor: string | null
    condition: string | null
    view: string | null
    has_elevator: boolean
    has_pertinenza: boolean
    has_good_exposure: boolean
    has_external_spaces: boolean
    has_garage: boolean
    has_pool: boolean
  } | null
}

interface UseLeadsRealtimeOptions {
  onNewLead: (lead: LeadWithValuation) => void
}

function playNotificationSound() {
  try {
    const ctx = new AudioContext()
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.connect(gain)
    gain.connect(ctx.destination)
    oscillator.frequency.value = 800
    oscillator.type = 'sine'
    gain.gain.value = 0.3
    oscillator.start()
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
    oscillator.stop(ctx.currentTime + 0.3)
  } catch {
    // Audio non disponibile
  }
}

function showBrowserNotification(lead: LeadWithValuation) {
  if (Notification.permission !== 'granted') return

  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Anonimo'
  const address = lead.valuation?.address || ''
  const value = lead.valuation
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(lead.valuation.estimated_value)
    : ''

  const body = [name, address, value].filter(Boolean).join(' - ')

  new Notification('Nuovo Lead!', {
    body,
    icon: '/favicon.ico',
    tag: 'new-lead',
  })
}

export function useLeadsRealtime({ onNewLead }: UseLeadsRealtimeOptions) {
  const [isConnected, setIsConnected] = useState(false)
  const [newLeadsCount, setNewLeadsCount] = useState(0)
  const channelRef = useRef<RealtimeChannel | null>(null)
  const onNewLeadRef = useRef(onNewLead)
  onNewLeadRef.current = onNewLead

  useEffect(() => {
    let channel: RealtimeChannel | null = null

    function subscribe() {
      // Rimuovi canale precedente se esiste
      if (channel) {
        channel.unsubscribe()
      }

      channel = supabase
        .channel('leads-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'leads_sanremo',
          },
          async (payload) => {
            const newRecord = payload.new as Record<string, unknown>

            // Fetch valutazione associata
            let valuation = null
            if (newRecord.valuation_id) {
              const { data } = await supabase
                .from('valuations_sanremo')
                .select('*')
                .eq('id', newRecord.valuation_id as string)
                .single()
              valuation = data
            }

            const lead: LeadWithValuation = {
              id: newRecord.id as string,
              email: newRecord.email as string,
              phone: (newRecord.phone as string) || null,
              first_name: (newRecord.first_name as string) || null,
              last_name: (newRecord.last_name as string) || null,
              gdpr_consent: newRecord.gdpr_consent as boolean,
              marketing_consent: newRecord.marketing_consent as boolean,
              status: (newRecord.status as string) || 'new',
              notes: (newRecord.notes as string) || null,
              created_at: newRecord.created_at as string,
              valuation_id: (newRecord.valuation_id as string) || null,
              valuation,
            }

            onNewLeadRef.current(lead)
            setNewLeadsCount((c) => c + 1)
            playNotificationSound()
            showBrowserNotification(lead)
          }
        )
        .subscribe((status) => {
          setIsConnected(status === 'SUBSCRIBED')
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            // Riprova dopo 3 secondi in caso di errore
            setTimeout(() => subscribe(), 3000)
          }
        })

      channelRef.current = channel
    }

    subscribe()

    // Riconnetti quando il tab torna visibile
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        subscribe()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (channel) channel.unsubscribe()
    }
  }, [])

  const resetNewLeadsCount = () => setNewLeadsCount(0)

  return { isConnected, newLeadsCount, resetNewLeadsCount }
}
